import { useGetCLAccountsQuery } from '@api/front-office/cl-accounts.api.rtk';
import ServerError from '@components/animation/server-error';
import DataTable from '@components/table/datatable';
import useDisclosure from '@hooks/useDisclosure';
import useMenu from '@hooks/useMenu';
import { CLAccountsDetailsList } from '@modules/front-office/interface/fo-cl-accounts-interface';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import {
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { useAppSelector } from '@store/index';
import { useEffect, useState } from 'react';
import FOCityLedgerAccountsBookings from './fo-cityledger-accounts-bookings';
import FoRoom_postingContainer from '@modules/front-office/containers/fo-room_posting.container';

const CustomToolbar = ({
  showZeroBalances,
  onShowZeroBalancesChange,
}: {
  showZeroBalances: boolean;
  onShowZeroBalancesChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Divider
        orientation='vertical'
        sx={{
          height: '36px',
          mr: '8px',
          display: { xs: 'none', sm: 'inline-block' },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showZeroBalances}
            onChange={(event) => onShowZeroBalancesChange(event.target.checked)}
          />
        }
        label='Show All Transactions'
      />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        sx={{
          ml: { xs: '0', sm: 'auto' },
          width: {
            xs: '100%!important',
            lg: '200px!important',
            xl: '220px!important',
          },
        }}
      />
    </GridToolbarContainer>
  );
};
const CLAccountsColumn: GridColDef[] = [
  {
    field: 'accountID',
    headerName: 'Account ID',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'accountType',
    headerName: 'Account Type',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'accountName',
    headerName: 'Account Name',
    minWidth: 100,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'nationality',
    headerName: 'Nationality',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'contact',
    headerName: 'Contact Number',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'emailAddress',
    headerName: 'Email',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'balance',
    headerName: 'Balance',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'lastPaid',
    headerName: 'Last Paid',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
];
const VisibleColumns = CLAccountsColumn.filter(
  (column) => !['accountID', 'accountType'].includes(column.field),
);

const FOCityLedgerAccountsTable = () => {
  const {
    handleContextMenu,
    selectedData,
    removeSelectedData,
    manualSetSelectedData,
    MenuWrapper,
    handleTouchEnd,
    handleTouchStart,
    handleClose,
  } = useMenu();

  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );

  const [clAccountsData, setCLAccountsData] = useState<CLAccountsDetailsList[]>(
    [] as CLAccountsDetailsList[],
  );

  const [showZeroBalances, setShowZeroBalances] = useState(false);

  const {
    data: clAccountsList,
    isError,
    isLoading,
    isFetching,
  } = useGetCLAccountsQuery(`?filter[accountType]=Individual`, {
    skip: selectedProperty.value === 'All Properties',
  });

  useEffect(() => {
    if (!clAccountsList?.data?.cityLedgerList?.length) return;
    const CLAccountsDetailsList = clAccountsList.data.cityLedgerList.map(
      (item) => item.cityLedger,
    );
    setCLAccountsData(CLAccountsDetailsList);
  }, [clAccountsList]);

  const filteredData = showZeroBalances
    ? clAccountsData
    : clAccountsData.filter((cityLedger) => cityLedger.balance !== '0.00');
  const clBookingsDrawerState = useDisclosure();
  const roomPostingDisclosure = useDisclosure();
  return (
    <>
      {isError ? (
        <ServerError />
      ) : (
        <DataTable
          getRowId={(row) => row.accountID}
          columns={VisibleColumns}
          rows={isError ? [] : filteredData || []}
          checkboxSelection={false}
          loading={isLoading || isFetching}
          pageSize={100}
          autoPageSize={false}
          onRowDoubleClick={(row) => {
            manualSetSelectedData(row.id.toString());
            clBookingsDrawerState.onOpen();
          }}
          tableStyle={{
            ".MuiButtonBase-root[aria-label='Export']": {
              display: 'none!important',
            },
          }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              onTouchStart: handleTouchStart,
              onTouchEnd: handleTouchEnd,
              style: { cursor: 'context-menu' },
            },
            toolbar: {
              showZeroBalances: showZeroBalances,
              onShowZeroBalancesChange: setShowZeroBalances,
            },
          }}
          slots={{
            toolbar: CustomToolbar,
          }}
          responsiveHeight
          sortBy='accountName'
        />
      )}
      <MenuWrapper>
        <MenuItem
          onClick={() => {
            roomPostingDisclosure.onOpen();
            handleClose();
          }}
        >
          Posting
        </MenuItem>
        <MenuItem
          onClick={() => {
            clBookingsDrawerState.onOpen();
            handleClose();
          }}
        >
          View Bookings
        </MenuItem>
      </MenuWrapper>
      <FOCityLedgerAccountsBookings
        accountID={selectedData}
        isOpen={clBookingsDrawerState.isOpen}
        onClose={() => {
          clBookingsDrawerState.onClose();
          removeSelectedData();
        }}
      />
      <FoRoom_postingContainer
        transactionDetails={{
          folioUuid: selectedData || '',
          folioStatus: '',
        }}
        onCloseModal={() => {
          roomPostingDisclosure.onClose();
        }}
        isOpenModal={roomPostingDisclosure.isOpen}
        transactionType={'Post Master'}
        isCityLedgerPosting={roomPostingDisclosure.isOpen}
      />
    </>
  );
};

export default FOCityLedgerAccountsTable;
